import {StyleSheet} from 'react-native';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';

// ── Design tokens ─────────────────────────────────────────────────────────────
export const PAGE_BG = CCColors.Primary.lightBrown;   // '#FAF6EB' warm cream
export const CARD_BG = CCColors.White;
export const CARD_BORDER = updateColorWithOpacity(CCColors.TextColor.Primary, 0.10);
export const CARD_BORDER_HOVER = updateColorWithOpacity(CCColors.TextColor.Primary, 0.24);

export const HERO_BG = CCColors.Workbook.DarkBrown;
export const HERO_ACCENT = CCColors.Primary.Action;   // '#FACF47'

// Deep banner colours — one per card (cycling by index)
export const CARD_BANNER_COLORS = [
  CCColors.Workbook.CategoryBlue,   // Deep navy
  CCColors.Workbook.CategoryGreenDark,   // Forest green
  CCColors.Workbook.CategoryRed,   // Burgundy
  CCColors.Workbook.CategoryPurple,   // Deep violet
  CCColors.Workbook.CategoryTeal,   // Dark teal
  CCColors.Workbook.CategoryBrown,   // Cognac
  CCColors.Workbook.CategoryPink,   // Bordeaux
  CCColors.Workbook.CategoryGreen,   // Hunter green
];

export const CARD_ICONS = [
  'chess-knight',
  'chess-bishop',
  'chess-rook',
  'chess-queen',
  'chess-king',
  'chess-pawn',
  'chess-knight',
  'chess-bishop',
];

export const styles = StyleSheet.create({
  // ── Root ─────────────────────────────────────────────────────────────────────
  container: {
    flex: 1,
    backgroundColor: PAGE_BG,
  },
  scrollContent: {
    paddingBottom: 72,
  },

  // ── Hero banner ──────────────────────────────────────────────────────────────
  heroBanner: {
    backgroundColor: CCColors.Workbook.HeroBannerBg,
    backgroundImage: `linear-gradient(135deg, ${CCColors.Workbook.HeroGradientStart} 0%, ${CCColors.Workbook.HeroGradientEnd} 100%)`,
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 16,           // was 24
    paddingHorizontal: 22,      // was 28
    paddingTop: 18,             // was 24
    paddingBottom: 18,          // was 24
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.Primary.Default, 0.08),
    position: 'relative',
  } as any,
  heroBreadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    backgroundColor: updateColorWithOpacity(CCColors.White, 0.04),
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.White, 0.08),
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginBottom: 12,
  },
  heroTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  heroIconRing: {
    width: 32, height: 32, borderRadius: 9,   // was 44/12
    backgroundColor: updateColorWithOpacity(HERO_ACCENT, 0.10),
    borderWidth: 1, borderColor: updateColorWithOpacity(HERO_ACCENT, 0.25),
    alignItems: 'center', justifyContent: 'center',
  },
  heroStatPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: updateColorWithOpacity(CCColors.White, 0.04),
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.White, 0.07),
    borderRadius: 7,
    paddingHorizontal: 9,
    paddingVertical: 4,
    flexShrink: 0,
  },
  heroDivider: {
    width: 1,
    height: 14,
    backgroundColor: updateColorWithOpacity(CCColors.White, 0.10),
  },
  heroCreateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: '#FACF47',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    shadowColor: '#FACF47',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 2,
  },
  heroSyncButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderWidth: 1.5,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  heroStatsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  heroActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
  },

  // ── Body ─────────────────────────────────────────────────────────────────────
  bodyWrapper: {
    paddingHorizontal: 16,
    paddingTop: 24,
  },
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 22,
    paddingBottom: 18,
    borderBottomWidth: 1,
    borderBottomColor: '#EAE2D5',
  },
  segmentWrapper: {
    flexDirection: 'row',
    backgroundColor: '#FFFDF9',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#EAE2D5',
    padding: 3,
    gap: 2,
  },
  segmentItem: {
    paddingHorizontal: 16,
    paddingVertical: 7,
    borderRadius: 20,
    backgroundColor: 'transparent',
  },
  segmentItemActive: {
    backgroundColor: '#2C1A11',
    shadowColor: '#2C1A11',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.15,
    shadowRadius: 4,
  },
  searchBox: {
    height: 38,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#EAE2D5',
    backgroundColor: CARD_BG,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    gap: 8,
    width: 260,
    shadowColor: '#000000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.02,
    shadowRadius: 4,
    elevation: 1,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: CCColors.TextColor.Dark,
    padding: 0,
    outlineStyle: 'none',
  } as any,
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 18,
  },
  sectionAccentLine: {
    width: 3,
    height: 18,
    borderRadius: 2,
    backgroundColor: CCColors.Primary.Action,
  },
  sectionCountBadge: {
    backgroundColor: updateColorWithOpacity(CCColors.Primary.Default, 0.18),
    borderRadius: 100,
    paddingHorizontal: 9,
    paddingVertical: 2,
    marginLeft: 2,
  },

  // ── Card grid ────────────────────────────────────────────────────────────────
  workbookGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // WORKBOOK CARD — new design
  // ═══════════════════════════════════════════════════════════════════════════

  // Outer shell
  workbookCard: {
    backgroundColor: CARD_BG,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: CARD_BORDER,
    overflow: 'hidden',
    position: 'relative',
    shadowColor: CCColors.Workbook.DarkBrown,
    shadowOffset: {width: 0, height: 3},
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 3,
    transitionProperty: 'all',
    transitionDuration: '0.22s',
    transitionTimingFunction: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  } as any,
  workbookCardHover: {
    borderColor: CARD_BORDER_HOVER,
    shadowOffset: {width: 0, height: 16},
    shadowOpacity: 0.18,
    shadowRadius: 36,
    elevation: 12,
    transform: [{translateY: -5}] as any,
  },

  // ── Card header strip (coloured, chess-themed) ────────────────────────────
  cardHeader: {
    paddingHorizontal: 16,
    paddingTop: 18,
    paddingBottom: 18,
    position: 'relative',
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // Left: serial + title
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  // Serial number ring
  serialRing: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: updateColorWithOpacity(CCColors.White, 0.12),
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.White, 0.22),
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  // Right: chess piece icon
  cardPieceIcon: {
    opacity: 0.13,
    position: 'absolute',
    right: -8,
    bottom: -14,
  },

  // ── Divider between header and body ──────────────────────────────────────
  cardDivider: {
    height: 1,
    backgroundColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.07),
    marginHorizontal: 16,
  },

  // ── Card body ─────────────────────────────────────────────────────────────
  cardBody: {
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: 0,
  },

  // Side-by-side row: owner left | chapters right
  cardBodyRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 0,
  },

  // Left owner column
  cardOwnerCol: {
    flex: 2,
    paddingRight: 10,
    gap: 6,
  },

  // Vertical hairline between columns
  cardBodyDivider: {
    width: 1,
    backgroundColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.09),
    marginVertical: 2,
  },

  // Right chapters column
  cardChaptersCol: {
    flex: 3,
    paddingLeft: 10,
    gap: 5,
  },

  // Shared uppercase section label (OWNER / CHAPTERS)
  colSectionLabel: {
    letterSpacing: 0.7,
    textTransform: 'uppercase',
    marginBottom: 4,
  } as any,

  // Owner name + avatar inline
  ownerBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  ownerAvatar: {
    width: 26,
    height: 26,
    borderRadius: 7,
    backgroundColor: updateColorWithOpacity(CCColors.Primary.Default, 0.15),
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.Primary.Default, 0.3),
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  ownerAvatarText: {
    fontSize: 11,
    fontWeight: '700',
    color: CCColors.Primary.Dark,
  },

  // Chapter count pill below owner name
  chapterCountBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: updateColorWithOpacity(CCColors.Primary.Default, 0.12),
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.Primary.Default, 0.25),
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 3,
    alignSelf: 'flex-start',
  },

  // Legacy label styles (kept for compat)
  chaptersLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  chaptersLabelLine: {
    width: 2,
    height: 11,
    borderRadius: 1,
    backgroundColor: CCColors.Primary.Action,
    opacity: 0.7,
  },

  // Chapter pill list
  chaptersList: {
    gap: 5,
    marginBottom: 0,
  },
  chapterPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: PAGE_BG,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.08),
  },
  chapterPillIndex: {
    width: 18,
    height: 18,
    borderRadius: 5,
    backgroundColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.10),
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreChaptersPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.07),
    borderStyle: 'dashed',
  },

  // ── Card footer ───────────────────────────────────────────────────────────
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 12,
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.07),
  },
  cardFooterLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  // Right side of footer: dot separator + delete button, side by side
  footerRightRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  // Small dot separator between date and delete
  footerDot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.20),
  },
  // Delete button
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.Workbook.DangerRed, 0.22),
    backgroundColor: updateColorWithOpacity(CCColors.Workbook.DangerRed, 0.04),
    transitionProperty: 'all',
    transitionDuration: '0.15s',
  } as any,
  deleteButtonHovered: {
    borderColor: updateColorWithOpacity(CCColors.Workbook.DangerRed, 0.50),
    backgroundColor: updateColorWithOpacity(CCColors.Workbook.DangerRed, 0.10),
  },

  // ── Empty state ───────────────────────────────────────────────────────────
  emptyCard: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
    paddingHorizontal: 24,
    backgroundColor: CARD_BG,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.10),
    borderStyle: 'dashed',
  },

  // ── Legacy / interface compat ─────────────────────────────────────────────
  contentWrapper: {flex: 1, padding: 24},
  headerContainer: {marginBottom: 24},
  tableCard: {
    marginTop: 16,
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: CARD_BORDER,
    backgroundColor: CARD_BG,
  },
  actionButton: {paddingHorizontal: 20, paddingVertical: 12, borderRadius: 8},
  metadataDivider: {width: 1, height: 10, backgroundColor: CARD_BORDER},
  workbookCardTop: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  watermarkIcon: {position: 'absolute', bottom: -8, right: -8},
  cardAccentStrip: {position: 'absolute', left: 0, top: 0, bottom: 0, width: 4},
  // kept for any external reference
  cardBanner: {height: 0},
  cardBannerRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  categoryBadge: {flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 100, backgroundColor: updateColorWithOpacity(CCColors.White, 0.14), borderWidth: 1, borderColor: updateColorWithOpacity(CCColors.White, 0.22)},
  categoryBadgeOwn: {},
  categoryBadgeShared: {},
  statusDot: {width: 5, height: 5, borderRadius: 2.5},
  statusDotOwn: {backgroundColor: CCColors.Workbook.SuccessBright},
  statusDotShared: {backgroundColor: CCColors.Primary.Action},
  indexBadge: {backgroundColor: updateColorWithOpacity(CCColors.White, 0.16), borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3, borderWidth: 1, borderColor: updateColorWithOpacity(CCColors.White, 0.26), minWidth: 30, alignItems: 'center'},
  cardBody_legacy: {flex: 1, paddingHorizontal: 14, paddingTop: 10, paddingBottom: 12, justifyContent: 'space-between'},
  workbookCardFooter: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%'},
  metadataItem: {flexDirection: 'row', alignItems: 'center', gap: 5},
  avatarCircle: {width: 22, height: 22, borderRadius: 11, backgroundColor: PAGE_BG, borderWidth: 1.5, borderColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.15), alignItems: 'center', justifyContent: 'center'},
  avatarText: {fontSize: 9, fontWeight: '700' as any, color: CCColors.TextColor.Primary},
  cardProgressBar: {height: 2, backgroundColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.08), borderRadius: 1, overflow: 'hidden', marginBottom: 8},
  cardProgressFill: {height: '100%' as any, backgroundColor: CCColors.Primary.Action, borderRadius: 1},

  // ── Filter UI styles ──────────────────────────────────────────────────────────
  filterButton: {
    height: 38,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#EAE2D5',
    backgroundColor: CARD_BG,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    gap: 8,
    shadowColor: '#000000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.02,
    shadowRadius: 4,
    elevation: 1,
    position: 'relative',
  },
  filterIndicator: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: CCColors.Workbook.SuccessBright,
  },
  filterPanel: {
    backgroundColor: '#FAF8F4',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#EAE2D5',
    padding: 20,
    marginBottom: 24,
    gap: 16,
    shadowColor: '#000000',
    shadowOffset: {width: 0, height: 8},
    shadowOpacity: 0.03,
    shadowRadius: 20,
    elevation: 4,
  },
  filterPanelMobile: {
    padding: 16,
  },
  filterPanelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: '#EAE2D5',
    paddingBottom: 12,
    marginBottom: 4,
  },
  filterSectionsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
    flexWrap: 'wrap',
  },
  filterSectionsRowMobile: {
    flexDirection: 'column',
    gap: 12,
  },
  filterSection: {
    flex: 1,
    minWidth: 220,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#EAE2D5',
    padding: 14,
  },
  filterChipsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#EAE2D5',
    shadowColor: '#000000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.02,
    shadowRadius: 2,
    elevation: 1,
  },
  filterChipActive: {
    backgroundColor: '#2C1A11',
    borderColor: '#2C1A11',
    shadowColor: '#2C1A11',
    shadowOpacity: 0.15,
    shadowRadius: 4,
  },

  // ── Dense List View Redesign ────────────────────────────────────────────────
  listContainer: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#EAE2D5',
    overflow: 'hidden',
    shadowColor: '#000000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.03,
    shadowRadius: 10,
    elevation: 2,
    marginBottom: 20,
  },
  listHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFDF9',
    borderBottomWidth: 1,
    borderBottomColor: '#EAE2D5',
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  listHeaderCell: {
    fontSize: 10.5,
    fontWeight: 'bold',
    color: '#9E9E9E',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  listRow: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#EAE2D5',
    position: 'relative',
    transitionProperty: 'background-color, border-left-color',
    transitionDuration: '0.15s',
  } as any,
  listRowContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    minHeight: 56,
  },
  listRowHover: {
    backgroundColor: '#FFFDF6',
  },
  listCell: {
    justifyContent: 'center',
  },
  listSerialBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  listSerialText: {
    fontSize: 10.5,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  listTitleText: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#1E110A',
  },
  listSubtitleText: {
    fontSize: 10.5,
    color: '#A39081',
    marginTop: 2,
  },
  listChaptersBadge: {
    backgroundColor: '#FFF8EB',
    borderColor: '#EAE2D5',
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignSelf: 'center',
  },
  listChaptersText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#4E3E34',
  },
  listPlatformContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    gap: 6,
  },
  listPlatformText: {
    fontSize: 11.5,
    color: '#1E110A',
    fontWeight: 'medium',
  },
  listVisibilityContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    gap: 6,
  },
  listVisibilityText: {
    fontSize: 11.5,
    color: '#4E3E34',
    fontWeight: 'medium',
  },
  listDateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    gap: 6,
  },
  listDateText: {
    fontSize: 11.5,
    color: '#757575',
  },
  listActionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  listActionButton: {
    padding: 6,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    transitionProperty: 'background-color, transform',
    transitionDuration: '0.12s',
  } as any,
  listConfirmDrawer: {
    backgroundColor: '#FFFDF9',
    borderTopWidth: 1,
    borderTopColor: '#EAE2D5',
    padding: 16,
    overflow: 'hidden',
  },
});